import React, { useRef, useState } from 'react';
import { ToneAnalysis, ToneScore } from '../../adherence/types';
import SummarySectionCard from './SummarySectionCard';
import { ProgressBar, scoreTone, toneTextClass } from '../../ui/ProgressBar';

interface ToneSummaryCardProps {
  toneAnalysis?: ToneAnalysis;
  onViewDetails: () => void;
}

interface ToneBarProps {
  label: string;
  toneScore: ToneScore;
  isPositive: boolean;
}

const ToneBar: React.FC<ToneBarProps> = ({ label, toneScore, isPositive }) => {
  // Same thresholds as the score badges; a high negative quality is the problem.
  const tone = scoreTone(toneScore.score, isPositive);
  const textColor = toneTextClass(tone);

  const barRef = useRef<HTMLDivElement>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);

  const handleMouseEnter = () => {
    if (!barRef.current || !toneScore.explanation) return;
    const rect = barRef.current.getBoundingClientRect();
    setTooltipPos({ x: rect.left, y: rect.top });
  };

  const handleMouseLeave = () => setTooltipPos(null);

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-ink-secondary">{label}</span>
        <span className={`text-xs font-bold ${textColor}`}>{toneScore.score}%</span>
      </div>
      <ProgressBar
        ref={barRef}
        value={toneScore.score}
        tone={tone}
        className="cursor-help"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />
      {tooltipPos && toneScore.explanation && (
        <div
          style={{
            position: 'fixed',
            left: tooltipPos.x,
            top: tooltipPos.y - 8,
            transform: 'translateY(-100%)',
            zIndex: 9999,
            maxWidth: 260,
          }}
          className="bg-ink text-white text-[11px] rounded-lg px-3 py-2 shadow-xl pointer-events-none"
        >
          <p className="leading-relaxed">{toneScore.explanation}</p>
          <p className="mt-1 opacity-70">AI confidence: {toneScore.confidence_level}%</p>
        </div>
      )}
    </div>
  );
};

const ToneSummaryCard: React.FC<ToneSummaryCardProps> = ({ toneAnalysis, onViewDetails }) => {
  if (!toneAnalysis?.positive) {
    return (
      <SummarySectionCard
        title="Tone (Sales Rep)"
        resultContent={null}
        missingDataMessage="Tone data not available. Re-analyze this session to generate tone analysis."
        onViewDetails={onViewDetails}
      />
    );
  }

  const { positive, negative, overall_coaching } = toneAnalysis;

  const highScore = Math.max(
    positive.confidence.score,
    positive.optimism.score
  );
  const lowConcern = Math.max(
    negative.defensiveness.score,
    negative.confrontation.score
  );

  const badgeColor = lowConcern >= 50
    ? 'bg-error-tint text-error border border-error'
    : highScore >= 60
    ? 'bg-success-tint text-success border border-success'
    : 'bg-warning-tint text-warning border border-warning';

  const badgeLabel = lowConcern >= 50 ? 'Needs Attention' : highScore >= 60 ? 'Strong Tone' : 'Moderate';

  const statusBadge = (
    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${badgeColor}`}>
      {badgeLabel}
    </span>
  );

  const resultContent = (
    <div className="space-y-3">
      <p className="text-[10px] font-semibold text-success uppercase tracking-wider">Positive</p>
      <div className="grid grid-cols-1 gap-3">
        <ToneBar label="Confidence" toneScore={positive.confidence} isPositive={true} />
        <ToneBar label="Optimism" toneScore={positive.optimism} isPositive={true} />
      </div>
      <p className="text-[10px] font-semibold text-error uppercase tracking-wider pt-1">Negative</p>
      <div className="grid grid-cols-1 gap-3">
        <ToneBar label="Defensiveness" toneScore={negative.defensiveness} isPositive={false} />
        <ToneBar label="Confrontation" toneScore={negative.confrontation} isPositive={false} />
      </div>
    </div>
  );

  return (
    <SummarySectionCard
      title="Tone (Sales Rep)"
      statusBadge={statusBadge}
      resultContent={resultContent}
      coachingItems={overall_coaching ? [overall_coaching] : undefined}
      sectionKey="tone"
      onViewDetails={onViewDetails}
    />
  );
};

export default ToneSummaryCard;
